import { Button, Card, CardContent, DataTable, Text } from "@/components/ui";
import { ServerErrorFallbackCard } from "@/components/ui/ServerErrorFallback/ServerErrorFallbackCard";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { retrieveMatterIntakeTemplateQuestionsOptions } from "@/lib/data/react-query/matters/matter-intake-template-question";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { Matter, MatterIntakeTemplateResponse } from "@/lib/types";
import { ErrorBoundary } from "@sentry/react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { type Row } from "@tanstack/react-table";
import { PlusCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { EditIntakeResponseModal } from "./IntakeResponseModal";
import { matterIntakeResponsesTableColumns } from "./matter-intake-responses-table-columns";

interface MatterInformationIntakeSectionProps {
  matter: Matter;
}

const MatterInformationIntakeSectionDangerous = ({
  matter,
}: MatterInformationIntakeSectionProps) => {
  const { hasActionPermission } = useUser();
  const { openIntakeResponseModal } = useGlobalModal();
  const [editSlideoverOpen, setEditSlideoverOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] =
    useState<MatterIntakeTemplateResponse | null>(null);
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const questionQueries = useQueries({
    queries: (matterSettingsData?.data.intakeTemplates ?? []).map((template) =>
      retrieveMatterIntakeTemplateQuestionsOptions(template.id),
    ),
  });
  const questionsById = useMemo(
    () =>
      new Map(
        questionQueries
          .flatMap((query) => query.data?.data ?? [])
          .map((question) => [question.id, question]),
      ),
    [questionQueries],
  );
  const responses = useMemo(
    () =>
      matter.intakeTemplateResponses.map((response) => ({
        ...response,
        question: response.question ?? questionsById.get(response.questionId),
      })),
    [matter.intakeTemplateResponses, questionsById],
  );

  const hasViewPermission = Boolean(true);
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIntakeTemplateResponse,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIntakeTemplateResponse,
    ACTION_PERMISSION_VALUES.create,
  );

  const handleCreateIntakeResponse = () => {
    openIntakeResponseModal({
      matterId: matter.id,
    });
  };

  const handleRowClick = (row: Row<MatterIntakeTemplateResponse>) => {
    if (hasUpdatePermission) {
      setSelectedResponse(row.original);
      setEditSlideoverOpen(true);
    }
  };

  return (
    <>
      <Card className="h-min">
        <CardContent>
          <div className="mt-4 mb-3 flex h-10 items-center gap-x-2">
            <Text variant="body" color="primary" className="font-semibold">
              Intake responses
            </Text>
            {hasCreatePermission && (
              <Button
                onClick={handleCreateIntakeResponse}
                variant="outline"
                className="ml-auto flex"
              >
                <PlusCircle className="w-4" />
                Create intake response
              </Button>
            )}
          </div>
          {!hasViewPermission && (
            <Text variant="subHeading" color="muted">
              You do not have permission to view intake responses.
            </Text>
          )}
          {hasViewPermission && (
            <DataTable
              entityLabelPlural="intake responses"
              columns={matterIntakeResponsesTableColumns}
              data={responses}
              onRowClick={handleRowClick}
              tableName={tanstackTableNames.matterIntakeResponses}
              noResultsMessage="No intake responses found."
              hideSearchBar
              hideVisiibilityDropdown
            />
          )}
        </CardContent>
      </Card>

      <EditIntakeResponseModal
        open={editSlideoverOpen}
        setOpen={setEditSlideoverOpen}
        response={selectedResponse}
        matterId={matter.id}
      />
    </>
  );
};

export function MatterInformationIntakeSection(
  props: MatterInformationIntakeSectionProps,
) {
  return (
    <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
      <MatterInformationIntakeSectionDangerous {...props} />
    </ErrorBoundary>
  );
}
